import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

export interface Atelier {
  id?: number;
  name: string;
  prix: string;
  description?: string;
  mainImageUrl?: string;
  galleryImages?: string[];
  openingTime?: string;       // format "HH:mm"
  closingTime?: string;
  activeDays?: number[];      // 1=Lundi, ..., 7=Dimanche
  sessionDurations?: number[]; // en minutes
  serviceId?: number;         // optionnel car passé en query
}

@Injectable({
  providedIn: 'root'
})
export class AtelierService {

  private base_path = 'https://app.titv-store-api.com/ateliers/';
  private refreshData = new Subject<void>();

  constructor(private http: HttpClient) { }

  get refreshNedeed() {
    return this.refreshData;
  }

  // ---------- Gestion du token et headers ----------
  private getToken(): string {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        const userObj = JSON.parse(authUser);
        return userObj.token || '';
      }
      return '';
    } catch {
      return '';
    }
  }

  private getHeaders(): HttpHeaders {
    const token = this.getToken();
    if (token) {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      });
    }
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Une erreur est survenue';
    if (error.status === 403) {
      errorMessage = `Accès interdit. Token: ${this.getToken() ? 'présent' : 'absent'}`;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else if (error.status === 404) {
      errorMessage = 'Atelier non trouvé.';
    } else {
      errorMessage = `Erreur ${error.status}: ${error.message}`;
    }
    console.error('Erreur AtelierService:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  // ---------- CRUD ----------

  /**
   * Créer un nouvel atelier
   * POST /ateliers?serviceId=...
   */
  createAtelier(atelier: Atelier, serviceId: number): Observable<Atelier> {
    const headers = this.getHeaders();
    // Pass serviceId as query parameter
    const params = new HttpParams().set('serviceId', serviceId.toString());
    // Remove serviceId from the body (it's sent as query param)
    const { serviceId: _, ...atelierWithoutServiceId } = atelier;
    return this.http.post<Atelier>(
      this.base_path, // just /ateliers/ (without 'add')
      JSON.stringify(atelierWithoutServiceId),
      { headers, params }
    ).pipe(
      tap(() => this.refreshData.next()),
      catchError(this.handleError)
    );
  }

  /**
   * Récupérer un atelier par son ID
   * GET /ateliers/{id}
   */
  getAtelier(id: number): Observable<Atelier> {
    const headers = this.getHeaders();
    return this.http.get<Atelier>(
      this.base_path + id,
      { headers }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Récupérer tous les ateliers (optionnellement filtrés par serviceId)
   * GET /ateliers/all?serviceId=...
   */
  getListAtelier(serviceId?: number): Observable<Atelier[]> {
    const headers = this.getHeaders();
    let params = new HttpParams();
    if (serviceId) {
      params = params.set('serviceId', serviceId.toString());
    }
    return this.http.get<Atelier[]>(
      this.base_path + 'all',
      { headers, params }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Mettre à jour un atelier
   * PUT /ateliers/edit/{id}
   */
 // atelier.service.ts

// atelier.service.ts

/**
 * Mettre à jour un atelier
 * PUT /ateliers/{id}?serviceId=...
 */
updateAtelier(id: number, item: any, serviceId: number): Observable<Atelier> {
  const headers = this.getHeaders();
  const params = new HttpParams().set('serviceId', serviceId.toString());
  return this.http.put<Atelier>(
    this.base_path + id,           // e.g., /ateliers/15715
    JSON.stringify(item),
    { headers, params }
  ).pipe(
    tap(() => this.refreshData.next()),
    catchError(this.handleError)   // this will trigger error callback
  );
}
  /**
   * Supprimer un atelier
   * DELETE /ateliers/delete/{id}
   */
  deleteAtelier(id: number): Observable<any> {
    const headers = this.getHeaders();
    return this.http.delete<any>(
      this.base_path + 'delete/' + id,
      { headers }
    ).pipe(
      tap(() => this.refreshData.next()),
      catchError(this.handleError)
    );
  }

  /**
   * Mettre à jour l'ordre d'affichage d'un atelier (si nécessaire)
   * PUT /ateliers/update-order/{id}
   */
  updateAtelierOrder(id: number, displayOrder: number): Observable<any> {
    const headers = this.getHeaders();
    return this.http.put(
      this.base_path + 'update-order/' + id,
      displayOrder,  // backend attend un Integer dans le body
      { headers, responseType: 'text' }
    ).pipe(
      tap(() => this.refreshData.next()),
      catchError(this.handleError)
    );
  }
}